import ViewViewModel from '@/ViewModels/ViewViewModel';
import { ISearchParameterDTO, ITaskInformationDTO, ITasksForTypeDTO } from "coreframetypings/dto";
import * as CoreFrameInterfaces from '#/CoreFrameInterfaces';
import StorageHelper from '@/Utility/StorageHelper';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import ComponentManager from '@/Management/ComponentManager';
import Config from '@/Management/GlobalConfiguration';
import NavigationHelper from '@/Management/NavigationHelper';
import NavigationService from '@/Management/NavigationService';
import RADApplication from '@/Management/RADApplication';

import AutoCompleteViewModelType from '@/ViewModels/Controls/AutoCompleteViewModel';
import ButtonViewModelType from '@/ViewModels/Controls/ButtonViewModel';
import DropDownListViewModelType from '@/ViewModels/Controls/DropDownListViewModel';
import ImageViewModelType from '@/ViewModels/Controls/ImageViewModel';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import LinkViewModelType from '@/ViewModels/Controls/LinkViewModel';
import LinkViewModelBuilder from '@/Builder/LinkViewModelBuilder';
import MenuViewModelType from '@/ViewModels/Controls/MenuViewModel';
import SearchBoxViewModelType from '@/Components/SearchView/SearchBox/SearchBoxViewModel';
import TreeViewViewModelType from '@/ViewModels/Controls/TreeViewViewModel';
import TreeViewItemViewModelType from '@/ViewModels/Controls/TreeViewItemViewModel';
import ResourceManager from '@/Management/ResourceManager';
import Utilities from '@/Utility/Utilities';
import PrincipalHelper from '@/Utility/PrincipalHelper';
import Loader from '@/Utility/Loader';
import UserDropdownMenuViewModelType from '@/Components/UserDropdownMenu/UserDropdownMenuViewModel';
import UserCircleLinkViewModelType from '@/Components/UserCircleLink/UserCircleLinkViewModel';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

/**
 * Repräsentiert das ViewModel des Header
 */
export default class HeaderViewModel extends ViewViewModel {

    private AutoCompleteViewModel = ComponentManager.getViewModel<typeof AutoCompleteViewModelType>('AutoCompleteViewModel');
    private ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModelType>('ButtonViewModel');
    private DropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModelType>('DropDownListViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelType>('ImageViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelType>('LinkViewModel');
    private MenuViewModel = ComponentManager.getViewModel<typeof MenuViewModelType>('MenuViewModel');
    private SearchBoxViewModel = ComponentManager.getViewModel<typeof SearchBoxViewModelType>('SearchBoxViewModel');
    private TreeViewViewModel = ComponentManager.getViewModel<typeof TreeViewViewModelType>('TreeViewViewModel');
    private TreeViewItemViewModel = ComponentManager.getViewModel<typeof TreeViewItemViewModelType>('TreeViewItemViewModel');
    private UserDropdownMenuViewModel = ComponentManager.getViewModel<typeof UserDropdownMenuViewModelType>('UserDropdownMenuViewModel');
    private UserCircleLinkViewModel = ComponentManager.getViewModel<typeof UserCircleLinkViewModelType>('UserCircleLinkViewModel');

    /**
     * Link zum Ein-/Ausblenden der Navigationsspalte
     */
    public togglePane: LinkViewModelType;

    /**
     * Zeigt den Anwendungsnamen an
     */
    public applicationName: LinkViewModelType;

    /**
     * Gibt an, ob das Menu angezeigt wird
     */
    public menuVisible: KnockoutObservable<boolean>;

    /**
     * Stellt das Anwendungsmenü dar
     */
    public userMenu: MenuViewModelType;

    /**
     * Link zum Ein-/Ausblenden des Menüs
     */
    public showMenu: LinkViewModelType;

    /**
     * Gibt an, ob die Favoriten angezeigt werden
     */
    public favoritesVisible: KnockoutObservable<boolean>;

    /**
     * Der TreeView mit den Favoriten-Einträgen
     */
    public favoriteTree: TreeViewViewModelType;

    /**
     * Link zum Ein-/Ausblenden der Favoriten
     */
    public showFavorites: LinkViewModelType;

    /**
     * Gibt an, ob die Tasks angezeigt werden
     */
    public tasksVisible: KnockoutObservable<boolean>;

    /**
     * Link zum Ein-/Ausblenden der Aufgaben
     */
    public showTasks: LinkViewModelType;

    /**
     * Gibt an, ob der Link zum Anzeigen der Aufgaben sichtbar ist
     */
    public showTasksVisible: KnockoutObservable<boolean>;

    /**
     * Link auf die Startseite
     */
    public linkHome: LinkViewModelType;

    /**
     * Link zum abmelden
     */
    public linkLogout: LinkViewModelType;

    /**
     * Gesamtanzahl der auf den User zugeordneten Tasks
     */
    public tasksCount: KnockoutComputed<string>;

    /**
     * Gesamtanzahl der für den User sichtbaren Tasks
     */
    public allTasksCount: KnockoutComputed<string>;

    /**
     * Total number of unassigned tasks
     */
    public unassignedTasksCount: KnockoutComputed<string>;

    /**
     * Gesamtanzahl der dem User nicht direkt zugeordneten Tasks
     */
    public extraTasksCount: KnockoutComputed<string>;

    /**
     * Der TreeView mit den Task-Einträgen
     */
    public tasksTree: TreeViewViewModelType;

    /**
     * Beinhaltet die Taks-Einträge des aktuellen Benutzers
     */
    public myTaskItems: KnockoutComputed<TreeViewItemViewModelType[]>;

    /**
     * Beinhaltet die Taks-Einträge für nicht zugewiesene Aufgaben
     */
    public unassignedTaskItems: KnockoutComputed<TreeViewItemViewModelType[]>;

    /**
     * Beinhaltet die Taks-Einträge für alle Aufgaben
     */
    public allTaskItems: KnockoutComputed<TreeViewItemViewModelType[]>;

    /**
     * Badge zur Anzeige der Anzahl an Aufgaben
     */
    public tasksBadge: LabelViewModelType;

    /**
     * Gibt an, ob das Badge zur Anzeige der Aufgaben sichtbar ist
     */
    public tasksBadgeVisible: KnockoutObservable<boolean>;

    public darkMode: LinkViewModelType;

    public darkModeActive: KnockoutObservable<boolean>;

    /**
     * Stellt den aktuellen Benutzernamen dar
     */
    public userName: LabelViewModelType;

    /**
     * Zeigt die Mandantenauswahl
     */
    public principal: DropDownListViewModelType;

    /**
     * Zeigt die Sprachauswahl
     */
    public language: DropDownListViewModelType;

    /**
     * Das Mandantenlogo
     */
    public image: ImageViewModelType;

    /**
     * Der Button zum Beginnen der Suche
     */
    public button: ButtonViewModelType;

    /**
     * Der aktuelle Wert der Suche
     */
    public searchValue: KnockoutObservable<string>;

    /**
     * Die Suchbox
     */
    public searchBox: SearchBoxViewModelType;

    /**
     * Die Suchparameter
     */
    public searchParameter: KnockoutObservable<any>;

    /**
     * Kreis-Link mit Initialen
     */
    public userCircleLink: UserCircleLinkViewModelType;

    /**
     * Gibt an, ob das UserMenu angezeigt werden soll
     */
    public showUserMenu: KnockoutObservable<boolean>;

    /**
     * User Menu
     */
    public userDropdownMenu: UserDropdownMenuViewModelType;

    public constructor(params: CoreFrameInterfaces.HeaderViewModelConstructorParameter) {
        super(params);

        this.searchParameter = this.observableOrDefault(null);
        this.togglePane = this.createTogglePaneLinkViewModel();
        this.applicationName = this.createApplicationNameLinkViewModel();
        this.menuVisible = this.observableOrDefault(false).extend({ waiter: 0 });
        this.userMenu = this.createUserMenu();
        this.defineSubscription(this.menuVisible, (val) => {
            if (this.userMenu)
                this.userMenu.closeAllItems();
        });
        this.showMenu = this.createShowSettingsLinkViewModel();
        this.favoritesVisible = this.observableOrDefault(false).extend({ waiter: 0 });

        const searchIsInFavorites = this.paramOrDefault((params as any).searchIsInFavorites, true);
        this.favoriteTree = this.createFavoriteTree(searchIsInFavorites);
        this.showFavorites = this.createShowFavoritesLinkViewModel();
        this.linkHome = this.createHomeLinkViewModel();
        this.linkLogout = this.createLogoutLinkViewModel();
        this.userName = this.createUserNameLabelViewModel();
        this.language = this.createLanguageDropDownListViewModel();
        this.principal = this.createPrincipalDropDownListViewModel();
        this.image = this.createPrincipalLogoViewModel();
        this.button = this.createSearchButtonViewModel();
        this.searchValue = this.observableOrDefault('');

        this.showUserMenu = this.observableOrDefault(false);
        this.userCircleLink = this.createUserCircleLinkViewModel();
        this.userDropdownMenu = this.createUserDropdownMenuViewModel();

        this.initTasks();
        this.initSearch();
    }

    /**
     * Erzeugt das Kreis LinkViewModel zum Ein- / Ausblenden des UserMenüs
     */
    protected createUserCircleLinkViewModel(): UserCircleLinkViewModelType {
        return new this.UserCircleLinkViewModel({
            parentLocator: this.locator,
            locator: 'user.initialen',
            onClick: () => {
                this.showUserMenu(!this.showUserMenu());
            }
        });
    }

    /**
     * Erzeugt das UserDropdownMenuViewModel für das UserMenu
     */
    protected createUserDropdownMenuViewModel(): UserDropdownMenuViewModelType {
        return new this.UserDropdownMenuViewModel({
            parentLocator: this.locator,
            locator: 'user.menu'
        });
    }

    /**
     * Erzeugt das LinkViewModel zum Ein- / Ausblenden des Menüs
     */
    protected createTogglePaneLinkViewModel(): LinkViewModelType {
        const locator = 'menu-toggle';
        const image = ko.observable('burger_collapse.svg')
        const isMobileView = Utilities.isMobileView();
        const expandedBinding = StorageHelper.bind('WireframeExpanded', !isMobileView);

        if (Utilities.isMobileView())
            this.toggleWireFrameExpanded(false); 
        else
            this.toggleWireFrameExpanded(true);

        this.defineSubscription(expandedBinding, newValue => {
            image(newValue ? 'burger_collapse.svg' : 'burger_expand.svg')
        })
        expandedBinding.notifySubscribers(expandedBinding());

        return new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: image,
                parentLocator: locator,
                locator: 'menutoggleImage',
                alternativeText: new this.LabelViewModel({
                    ergName: 'TOGGLE_MENU'
                })
            }),
            onClick: () => {
                const expand = !expandedBinding();
                expandedBinding(expand)
                StorageHelper.set("NavigationRegionWidth", expand ? 300 : 0, true);
                $(window).trigger('resize');
            },
            parentLocator: this.locator,
            locator: locator
        });
    }

    /**
     * Erzeugt das LabelViewModel für den Applikationsnames
     */
    protected createApplicationNameLinkViewModel(): LinkViewModelType {
        return new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: this.resolveImage(),
                alternativeText: new this.LabelViewModel({
                    ergName: 'COMPANY_HEADER'
                })
            }),
            label: new this.LabelViewModel({
                caption: RADApplication.getInstance().appName
            }),
            onClick: (openInNewWindow) => {
                NavigationService.getInstance().navigateToUrl('Home', null, null, null, null, openInNewWindow);
            }
        });
    }

    private resolveImage(): KnockoutComputed<string> {
        return this.defineComputed(() => {
            const principalList = RADApplication.getInstance().user.principalList();
            const principal = RADApplication.getInstance().user.principal();

            if (!Config.headerView.usePrincipalLogoAsCompanyHeader || Utilities.isNullOrUndefinedOrBlank(principal))
                return Config.inlineImages.COMPANY_HEADER_LOGO_SMALL;

            for (let i = 0; i < principalList.length; i++) {
                if (principalList[i].name === principal) {
                    // ist kein Mandantenlogo gesetzt - gib als Fallback das Applikations-Logo zuück, andernfalls lade das Mandantenlogo
                    return Utilities.isNullOrUndefinedOrBlank(principalList[i].logoUrl)
                        ? Config.inlineImages.COMPANY_HEADER_LOGO_SMALL
                        : principalList[i].logoUrl;
                }
            }
        });
    }

    /**
     * Erzeugt TreeViewModel für das UserMenü
     */
    protected createUserMenu(): MenuViewModelType {
        return this.MenuViewModel.parseUserMenuTree();
    }

    /**
     * Erzeugt das LinkViewModel zum Aufruf der Einstellungen
     */
    protected createShowSettingsLinkViewModel(): LinkViewModelType {
        const locator = 'menu.settings';
        return new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: Config.inlineImages.GEAR,
                parentLocator: locator,
                locator: 'settingsImage',
                alternativeText: new this.LabelViewModel({
                    ergName: 'COMPANY_HEADER'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'OPTIONS_LANDSCAPE_TITLE_TOOLTIP'
            }),
            onClick: (openInNewWindow) => {
                NavigationService.getInstance().navigateToUrl('Settings', null, null, null, null, openInNewWindow);
            },
            locator: locator,
            hotkey: 'alt+o'
        });
    }

    /**
     * Erzeugt das TreeViewViewModel für die Favoriten
     */
    protected createFavoriteTree(searchIsInFavorites: boolean): TreeViewViewModelType {
        return this.TreeViewViewModel.parseUserFavoriteTree(searchIsInFavorites);
    }

    /**
     * Erzeugt das LinkViewModel zum Aufrufen der Favoriten
     */
    protected createShowFavoritesLinkViewModel(): LinkViewModelType {
        const locator = 'menu.favorites';
        return new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: Config.inlineImages.FAVORITES,
                parentLocator: locator,
                locator: 'favoritesImage',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MENU_ITEM_FAVORITES_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MENU_ITEM_FAVORITES_TOOLTIP'
            }),
            isEnabled: this.defineComputed(() => {
                return this.favoriteTree != null && this.favoriteTree.items().length > 0;
            }),
            onClick: () => {
                const isVisible = this.favoritesVisible();
                this.favoritesVisible(!isVisible);
            },
            locator: locator
        });
    }

    /**
     * Erzeugt ein Observable, welches angibt, ob das Icon für die Aufgaben sichtbar sein soll
     */
    protected createShowTasksVisibleObservable(): KnockoutObservable<boolean> {
        return this.observableOrDefault(true);
    }

    /**
     * Erzeugt das LinkViewModel zum Aufrufen der Aufgaben
     */
    protected createShowTasksLinkViewModel(): LinkViewModelType {
        return new LinkViewModelBuilder()
            .withImage().withSource(Config.inlineImages.SHOW_TASKS).withAlternativeTextErgName('MENU_ITEM_TASKS_TOOLTIP').withLocator('tasksImage').done()
            .withOnClick(() => {
                const isVisible = this.tasksVisible();
                this.tasksVisible(!isVisible);
            })
            .withIsVisible(this.createShowTasksVisibleObservable())
            .withLocator('menu.tasks')
            .withTooltipErgName('MENU_ITEM_TASKS_TOOLTIP')
            .build();
    }

    /**
     * Erzeugt das LinkViewModel zum Aufrufen der Startseite
     */
    protected createHomeLinkViewModel(): LinkViewModelType {
        const locator = 'menu.home';
        return new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: Config.inlineImages.MENU_HOME,
                parentLocator: locator,
                locator: 'homeImage',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MENU_ITEM_HOME_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MENU_ITEM_HOME_TOOLTIP',
            }),
            onClick: (openInNewWindow) => {
                NavigationService.getInstance().navigateToUrl('Home', null, null, null, null, openInNewWindow);
            },
            locator: locator
        });
    }

    /**
     * Erzeugt das LinkViewModel zum Aufrufen des Logouts
     */
    protected createLogoutLinkViewModel(): LinkViewModelType {
        return new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: 'standby.png',
                locator: 'static-menu.logout.link.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MENU_ITEM_LOGOUT_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MENU_ITEM_LOGOUT_TOOLTIP'
            }),
            onClick: () => {
                NavigationService.getInstance().loadViewFactory('Logout');
            }
        });
    }

    /**
     * Gibt zurück, ob die ViewModels für die Aufgabenverwaltung initialisiert werden sollen
     */
    protected shouldInitTasks(): boolean {
        return RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowTasks');
    }

    /**
     * Gibt an, ob der Header kompakt dargestellt werden soll
     * Anwendungssuche, Home-Button, Favoriten sowie Tasks werden ausgeblendet
     */
    protected isComppact(): boolean {
        return Utilities.isMobileView();
    }

    /**
     * Initialisiert die Berechnungen und ViewModels für die Aufgabenverwaltung
     */
    protected initTasks(): void {
        if (!this.shouldInitTasks())
            return;

        this.tasksVisible = this.observableOrDefault(false).extend({ waiter: 0 });
        this.showTasksVisible = this.createShowTasksVisibleObservable();
        this.showTasks = this.createShowTasksLinkViewModel();
        this.createTaskComputation();
        this.myTaskItems = this.createMyTasksItems();
        this.unassignedTaskItems = this.createUnassignedTasksItems();
        this.allTaskItems = this.createAllTasksItems();
        this.tasksTree = this.createTasksTree();
        this.tasksBadgeVisible = this.createTasksBadgeVisibleObservable();
        this.tasksBadge = this.createTasksBadgeViewModel();
    }

    /**
     * Startet und aktiviert die Berechnung der Aufgaben
     */
    protected createTaskComputation(): void {
        this.tasksCount = this.definePureComputed({
            read: () => {
                let tasksCount = 0;
                const taskInformation = RADApplication.getInstance().user.taskInformation();

                if (taskInformation != null && taskInformation.allAssignedTasks) {
                    tasksCount = this.getTasksSum(taskInformation.allAssignedTasks);
                }

                return this.getTasksCount(tasksCount);
            }
        });

        this.allTasksCount = this.definePureComputed({
            read: () => {
                let tasksCount = 0;
                const taskInformation = RADApplication.getInstance().user.taskInformation();

                if (taskInformation != null && taskInformation.allTasks) {
                    tasksCount = this.getTasksSum(taskInformation.allTasks);
                }

                return this.getTasksCount(tasksCount);
            }
        });

        this.unassignedTasksCount = this.definePureComputed({
            read: () => {
                let tasksCount = 0;
                const taskInformation = RADApplication.getInstance().user.taskInformation();

                if (taskInformation != null && taskInformation.allUnassignedTasks) {
                    tasksCount = this.getTasksSum(taskInformation.allUnassignedTasks);
                }

                return this.getTasksCount(tasksCount);
            }
        });

        this.extraTasksCount = this.definePureComputed({
            read: () => {
                let extraTasksCount = 0;
                const taskInformation = RADApplication.getInstance().user.taskInformation();

                if (taskInformation != null) {
                    extraTasksCount = Math.max(0, this.getTasksSum(taskInformation.allTasks) - this.getTasksSum(taskInformation.allAssignedTasks));
                }

                return this.getTasksCount(extraTasksCount);
            }
        });
    }

    private getTasksCount(count: number): string {
        if (count > 99) {
            return '99+';
        }

        return count + '';
    }

    /**
     * Erzeugt die TreeViewItemViewModels für die eigenen Aufgaben
     */
    protected createMyTasksItems(): KnockoutComputed<TreeViewItemViewModelType[]> {
        return this.createTasksItems('allAssignedTasks', this.getMyTasksStaticFilter());
    }

    /**
     * Erzeugt die TreeViewItemViewModels für die eigenen Aufgaben
     */
    protected createUnassignedTasksItems(): KnockoutComputed<TreeViewItemViewModelType[]> {
        return this.createTasksItems('allUnassignedTasks', this.getUnassginedTasksStaticFilter());
    }

    /**
     * Erzeugt die TreeViewItemViewModels für die eigenen Aufgaben
     */
    protected createAllTasksItems(): KnockoutComputed<TreeViewItemViewModelType[]> {
        return this.createTasksItems('allTasks', this.getAllTasksStaticFilter());
    }

    private createTasksItems(key: keyof ITaskInformationDTO, filter: string): KnockoutComputed<TreeViewItemViewModelType[]> {
        return this.defineComputed(() => {
            let result = [];

            const tasks = RADApplication.getInstance().user.taskInformation()[key] as ITasksForTypeDTO[];

            tasks.sort((a: ITasksForTypeDTO, b: ITasksForTypeDTO) => {
                if (Utilities.isNullOrUndefinedOrBlank(a.pdTypeId))
                    return 1;

                if (Utilities.isNullOrUndefinedOrBlank(b.pdTypeId))
                    return -1;

                return ResourceManager.getInstance().get(a.ergNamePluralized).localeCompare(ResourceManager.getInstance().get(b.ergNamePluralized));
            });

            tasks.forEach((tasksForType) => {
                result.push(this.createTreeViewItemForType(tasksForType, filter));
            });

            return result;
        });
    }

    /**
     * Erzeugt das TreeViewViewModel für die Aufgaben
     */
    protected createTasksTree(): TreeViewViewModelType {
        this.defineSubscription(this.tasksCount, (taskCount) => {
            this.reRenderTasksTree();
        });

        this.defineSubscription(this.allTasksCount, (taskCount) => {
            this.reRenderTasksTree();
        });

        this.defineSubscription(this.unassignedTasksCount, (taskCount) => {
            this.reRenderTasksTree();
        });

        this.defineSubscription(this.myTaskItems, (someUpdate) => {
            this.reRenderTasksTree();
        });

        const treeViewViewModel = new this.TreeViewViewModel({
            items: this.getTaskTreeItems(),
            hasSearchFeature: false
        });

        return treeViewViewModel;
    }

    /**
     * Löst ein rerendern des Aufgabenbaums aus
     */
    protected reRenderTasksTree(): void {
        this.tasksTree.items(this.getTaskTreeItems());
    }

    /**
     * Erzeugt die TreeViewItemViewModels für die Aufgaben
     */
    protected getTaskTreeItems(): TreeViewItemViewModelType[] {
        const myTasksTreeViewItemLinkLabel = new this.LabelViewModel({
            locator: 'menu.tasks.myTasks.label',
            ergName: 'TASKS_MY_TASKS',
            replacements: {
                counttasks: new this.LabelViewModel({
                    caption: this.tasksCount as any
                })
            }
        });

        const myTasksTreeViewItemLink = new this.LinkViewModel({
            locator: 'menu.tasks.myTasks',
            image: new this.ImageViewModel({
                source: Config.inlineImages.MYTASKS,
                alternativeText: new this.LabelViewModel({
                    ergName: 'TASKS_MY_TASKS'
                })
            }),
            label: myTasksTreeViewItemLinkLabel,
            onClick: (openInNewWindow: boolean) => {
                this.navigateToTasks({ filter: this.getMyTasksStaticFilter() }, openInNewWindow);
            }
        });

        const myTasksTreeViewItem = new this.TreeViewItemViewModel({
            isExpanded: true,
            link: myTasksTreeViewItemLink,
            items: this.myTaskItems
        });

        const unassignedTasksTreeViewItem = new this.TreeViewItemViewModel({
            link: new this.LinkViewModel({
                locator: 'menu.tasks.unassignedTasks',
                image: new this.ImageViewModel({
                    source: Config.inlineImages.UNASSIGNEDTASKS,
                    alternativeText: new this.LabelViewModel({
                        ergName: 'TASKS_UNASSIGNED_TASKS'
                    })
                }),
                label: new this.LabelViewModel({
                    locator: 'menu.tasks.unassignedTasks.label',
                    ergName: 'TASKS_UNASSIGNED_TASKS_COUNTED',
                    replacements: {
                        counttasks: new this.LabelViewModel({
                            caption: this.unassignedTasksCount as any
                        })
                    }
                }),
                onClick: (openInNewWindow: boolean) => {
                    this.navigateToTasks({ filter: this.getUnassginedTasksStaticFilter() }, openInNewWindow);
                }
            }),
            items: this.unassignedTaskItems
        });

        const allTasksTreeViewItem = new this.TreeViewItemViewModel({
            link: new this.LinkViewModel({
                locator: 'menu.tasks.allTasks',
                image: new this.ImageViewModel({
                    source: Config.inlineImages.ALLTASKS,
                    alternativeText: new this.LabelViewModel({
                        ergName: 'TASKS_ALL_TASKS'
                    })
                }),
                label: new this.LabelViewModel({
                    locator: 'menu.tasks.allTasks.label',
                    ergName: 'TASKS_ALL_TASKS_COUNTED',
                    replacements: {
                        countalltasks: new this.LabelViewModel({
                            caption: this.allTasksCount as any
                        })
                    }
                }),
                onClick: (openInNewWindow: boolean) => {
                    this.navigateToTasks({ filter: this.getAllTasksStaticFilter() }, openInNewWindow);
                }
            }),
            items: this.allTaskItems
        });

        return [
            myTasksTreeViewItem,
            unassignedTasksTreeViewItem,
            allTasksTreeViewItem
        ];
    }

    /**
     * Liefert den statischen Filter für "Meine Aufgaben"
     */
    protected getMyTasksStaticFilter(): string {
        return `this.Assignee.PDO_ID=${RADApplication.getInstance().user.pdoId()} AND this.IsCompleted=false`;
    }

    /**
     * Returns the static filter for 'Unassigned Tasks'
     */
    protected getUnassginedTasksStaticFilter(): string {
        return `this.Assignee is null AND this.IsCompleted=false`;
    }

    /**
     * Liefert den statischen Filter für "Alle Aufgaben"
     */
    protected getAllTasksStaticFilter(): string {
        return 'this.IsCompleted=false';
    }

    /**
     * Navigiert zu der Liste der Aufgaben mit den übergebenen Parametern
     * @param filter
     * @param openInNewWindow
     */
    protected navigateToTasks(params: CoreFrameInterfaces.IListViewParameter, openInNewWindow: boolean): void {
        const showIndependent = RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowTasks_PrincipalIndependent');
        if (showIndependent)
            params.principalFilterMode = PrincipalHelper.principalFilterAllClient;

        const pdTaskTypeName = 'RAD.RADFoundation.ProblemDomain.PDTask';
        NavigationHelper.openListViewByType(pdTaskTypeName, params, openInNewWindow);
    }

    /**
     * Erzeugt das LabelViewModel für die Taskbadge
     */
    protected createTasksBadgeViewModel(): LabelViewModelType {
        return new LabelViewModelBuilder()
            .withLocator('menu.tasks.badge')
            .withIsVisible(this.tasksBadgeVisible)
            .withCaption(this.defineComputed(() => {
                const myTasks = this.tasksCount();
                const extraTasks = this.extraTasksCount();

                let result = myTasks + '';
                if (extraTasks !== '0') {
                    result += `/${extraTasks}`;
                }
                return result;
            }))
            .build();
    }

    /**
     * Erzeugt ein Observable, welches angibt, ob das Badge sichtbar sein soll
     */
    protected createTasksBadgeVisibleObservable(): KnockoutObservable<boolean> {
        return this.defineComputed(() => {
            const taskInformation = RADApplication.getInstance().user.taskInformation();
            return taskInformation != null && this.getTasksSum(taskInformation.allTasks) > 0;
        });
    }

    /**
     * Erzeugt das LabelViewModel für den Usernamen
     */
    protected createUserNameLabelViewModel(): LabelViewModelType {
        return new this.LabelViewModel({
            caption: this.defineComputed(() => {
                return RADApplication.getInstance().user.lastName() + ', ' + RADApplication.getInstance().user.firstName();
            }),
            isEnabled: this.defineComputed(() => {
                return RADApplication.getInstance().currentDetailViewContext() == null;
            }),
        });
    }

    /**
     * Erzeugt das DropDownListViewModel für die Sprachauswahl
     */
    protected createLanguageDropDownListViewModel(): DropDownListViewModelType {
        return new this.DropDownListViewModel({
            value: StorageHelper.bindGlobal('lastLoggedInLanguage', null),
            data: this.defineComputed(() => ResourceManager.getInstance().getLanguagesForLogin()),
            isEnabled: this.defineComputed(() => {
                return RADApplication.getInstance().currentDetailViewContext() == null;
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MENU_LANGUAGE_NODE_NAME'
            }),
            ariaLabel: ResourceManager.getInstance().get('MENU_LANGUAGE_NODE_NAME'),
            dataTextField: 'iso6391',
            dataValueField: 'iso6391',
            template: '# if (data) { #<span>#:data.iso6391# - #:data.displayName#</span># } #',
            styles: ['language'],
            parentLocator: this.locator,
            locator: 'LANGUAGEDROPDOWN',
            onSelect: this.onSelectedLanguageChanged.bind(this),
            isVisible: this.defineComputed(() => {
                return ResourceManager.getInstance().getLanguagesForLogin().length > 1;
            })
        });
    }

    /**
     * Wird beim Auswählen einer Sprache gefeuert
     * @param languageIso
     */
    protected onSelectedLanguageChanged(languageIso: string): void {
        if (languageIso == ResourceManager.getInstance().language.value())
            return;

        Loader.getInstance().show();
        RADEnvironmentProxy.getInstance().changeLanguage({
            lang: languageIso
        })
            .then(() => {
                NavigationService.getInstance().reload(false, false);
            })
            .always(() => {
                Loader.getInstance().hide()
            });
    }

    /**
     * Erzeugt das DropDownListViewModel für die Mandantenauswahl
     */
    protected createPrincipalDropDownListViewModel(): DropDownListViewModelType {
        return new this.DropDownListViewModel({
            value: RADApplication.getInstance().user.principal,
            data: RADApplication.getInstance().user.principalList,
            isEnabled: this.defineComputed(() => {
                return RADApplication.getInstance().currentDetailViewContext() == null;
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'PRINCIPAL_MENU_TOOLTIP'
            }),
            ariaLabel: ResourceManager.getInstance().get('PRINCIPAL_NAME'),
            dataTextField: 'name',
            dataValueField: 'name',
            styles: ['principal'],
            parentLocator: this.locator,
            locator: 'PRINCIPALDROPDOWN'
        });
    }

    /**
     * Erzeugt das ImageViewModel für das Mandantenlogo
     */
    protected createPrincipalLogoViewModel(): ImageViewModelType {
        return new this.ImageViewModel({
            parentLocator: this.locator,
            locator: 'principal.logo',
            isVisible: this.defineComputed(() => {
                if (Config.headerView.usePrincipalLogoAsCompanyHeader)
                    return false;

                const principalList = RADApplication.getInstance().user.principalList();
                const principal = RADApplication.getInstance().user.principal();

                for (let i = 0; i < principalList.length; i++) {
                    if (principalList[i].name === principal) {
                        return principalList[i].logoUrl && principalList[i].logoUrl.indexOf('BLANK') === -1;
                    }
                }

                return false;
            }),
            source: this.defineComputed(() => {
                const principalList = RADApplication.getInstance().user.principalList();
                const principal = RADApplication.getInstance().user.principal();

                for (let i = 0; i < principalList.length; i++) {
                    if (principalList[i].name === principal) {
                        return principalList[i].logoUrl;
                    }
                }
            }),
            alternativeText: new this.LabelViewModel({
                ergName: 'RAD.RADFOUNDATION.PROBLEMDOMAIN.PDPRINCIPAL_LOGO_ERGNAME'
            })
        });
    }

    /**
     * Erzeugt das ButtonViewModel zum Starten der Suche
     */
    protected createSearchButtonViewModel(): ButtonViewModelType {
        return new this.ButtonViewModel({
            onClick: () => {
                this.searchBox.onSubmit(this.searchValue());
            }
        });
    }

    /**
     * Initialisiert die Suche
     */
    protected initSearch(): void {
        RADEnvironmentProxy.getInstance().getAvailableSearchParameters()
            .then((searchParameterDto: ISearchParameterDTO) => {
                if (this.isDisposed) {
                    return;
                }

                this.onSearchParameterLoaded(searchParameterDto);
            });
    }

    /**
     * Wird aufgerufen, wenn die SearchParamater geladen wurden
     * Ruft die verfügbaren Suchparameter ab und initialisiert die SearchBox
     * @param searchParameterDto
     */
    protected onSearchParameterLoaded(searchParameterDto: ISearchParameterDTO): void {
        this.searchBox = this.createSearchBoxViewModel(searchParameterDto);
        this.searchParameter(searchParameterDto);
    }

    /**
     *  Erzeugt das SearchBoxViewModel zur Eingabe der Suche
     * @param searchParameterDto
     */
    protected createSearchBoxViewModel(searchParameterDto: ISearchParameterDTO): SearchBoxViewModelType {
        // TODO: Hierfür könnte man ein HeaderSearchBoxViewModel erzeugen
        let selectedText = null;
        const loadData = this.observableOrDefault(false);
        const sb = new this.SearchBoxViewModel({
            extensionModules: searchParameterDto.extensionModules,
            historizationPoints: searchParameterDto.historizationPoints,
            isVisible: Config.headerView.showApplicationSearch,
            autoComplete: new this.AutoCompleteViewModel({
                // Potentielles Memory Leak: AsyncComputed sollte in Class.ts implementiert werden
                data: this.defineAsyncComputed(() => {
                    if (!loadData())
                        return [];
                    let query = null;

                    query = this.searchValue();

                    if (selectedText != null) {
                        this.searchBox.autoComplete.value(selectedText);
                        selectedText = null;
                        return [];
                    }

                    if (query && query.length >= 3) {
                        return RADEnvironmentProxy.getInstance().getSearchSuggestions({
                            query: this.searchBox.buildSearchQuery(100, query)
                        }).then((suggestionsDTO) => {
                            if (this.validateStatus(suggestionsDTO)) {
                                return suggestionsDTO.suggestions;
                            }

                            return [] as string[];
                        });
                    }
                    else if (!query && sb)
                        return sb.lastSearchValues();
                    else
                        return [];
                }, []),
                value: this.searchValue,
                onSelect: (event) => {
                    selectedText = event.item.text();
                },
                onKeyUp: (query) => {
                    this.searchBox.autoComplete.data.evaluate();
                },
                filter: () => {
                    return true;
                },
                onSubmit: (query: string) => {
                    this.searchBox.onSubmit(query);
                },
                parentLocator: this.locator,
                locator: 'searchbox.autocomplete',
                minLength: 0,
                searchOnFocus: true,
                ariaLabel: ResourceManager.getInstance().get('SEARCH_TITLE')
            }),
            onSubmit: (query: string) => {
                NavigationHelper.openSearch(query);
                this.searchBox.clear();
            },
            parentLocator: this.locator,
            locator: 'searchbox'
        });

        loadData(true);

        return sb;
    }

    /**
     * Hilfsmethode zum Erstellen von TreeViewItemViewModels für Aufgaben eines Typs
     * @param task
     * @param viewId
     */
    private createTreeViewItemForType(tasksForType: ITasksForTypeDTO, filter: string): TreeViewItemViewModelType {
        return new this.TreeViewItemViewModel({
            link: new this.LinkViewModel({
                image: new this.ImageViewModel({
                    source: Config.inlineImages.TASK,
                    alternativeText: new this.LabelViewModel({
                        ergName: 'RAD.RADFOUNDATION.PROBLEMDOMAIN.PDTASK_ERGNAME'
                    })
                }),
                label: new this.LabelViewModel({
                    caption: `${ResourceManager.getInstance().get(tasksForType.ergNamePluralized)} (${this.getTasksCount(tasksForType.count)})`
                }),
                onClick: (openInNewWindow: boolean) => {
                    const typeId = tasksForType.pdTypeId;
                    const typeFilter = Utilities.isNullOrUndefinedOrBlank(typeId)
                        ? 'this.PersistedPrimaryInputObjectPdoTypeId is null'
                        : `this.PersistedPrimaryInputObjectPdoTypeId = ${typeId}`;

                    this.navigateToTasks({ filter: `${filter} AND ${typeFilter}` }, openInNewWindow);
                }
            })
        });
    }

    private getTasksSum(dtos: ITasksForTypeDTO[]) {
        return _.reduce(dtos.map(x => x.count), (a, b) => a + b, 0);
    }

    private toggleWireFrameExpanded(showWireFrame: boolean) {
        StorageHelper.set('WireframeExpanded', showWireFrame);
        $(window).trigger('resize');
    }
}

ComponentManager.setViewModel('HeaderViewModel', HeaderViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');